import { fInventoryList } from "@/apis/factoryInventory";
import useDataList from "@/hooks/useDataList";
import STableLayout, { DEFAULT_PAGE_SIZE, TableLayoutRef } from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { CommonListParams } from "@/types/common";
import { FactoryInventory } from "@/types/factoryInventory";
import { Flex, Input, Modal, notification, Spin, TableProps } from "antd";
import { AnyObject } from "antd/es/_util/type";
import { FC, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

interface SelectFactoryProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: FactoryInventory[]) => void;
}
const SelectFactory: FC<SelectFactoryProps> = ({ open, onClose, onConfirm }) => {
  const tableLayoutRef = useRef<TableLayoutRef>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const { t } = useTranslation();

  const [list, total, loading, { fetchList }] = useDataList<FactoryInventory, [CommonListParams]>({ fetchApi: fInventoryList });

  const [keyword, setKeyword] = useState<string>('');
  const [selectedRows, setSelectedRows] = useState<FactoryInventory[]>([]);

  useEffect(() => {
    fetch(0);
    return () => abortControllerRef.current?.abort();
  }, []);

  const fetch = (offset: number) => {
    if (offset === 0) tableLayoutRef.current?.resetTablePage();
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;
    fetchList({
      body: '',
      strs: !!keyword ? keyword.split(' ') : undefined,
      pagination: {
        offset,
        take: tableLayoutRef.current?.pageSize ?? DEFAULT_PAGE_SIZE,
      },
      controller
    })
  };

  const handleOk = () => {
    if (selectedRows.length > 0) onConfirm(selectedRows)
    else return notification.error({ message: t('product.selProduct') });
  };

  const columns: TableProps<FactoryInventory>['columns'] = [
    {
      title: <SText text={t("product.code")} />,
      key: "productCode",
      width: 120,
      render: (_, record) => (
        <SText
          text={record.product?.code || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("product.name")} />,
      key: "productName",
      width: 150,
      render: (_, record) => (
        <SText
          text={record.product?.name || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("sale.prodMod")} />,
      key: "productModel",
      width: 220,
      render: (_, record) => (
        <SText
          text={record.product?.model || ""}
          style={{ width: "100%" }}
        />
      )
    },
    {
      title: <SText text={t("product.uom")} />,
      key: "uom",
      width: 80,
      align: "center",
      render: (_, record) => (
        <SText text={record.product?.uom || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("product.materialProcess")} />,
      key: "materialProcess",
      width: 80,
      render: (_, record) => (
        <SText text={record.product?.process || ""} style={{ width: "100%" }} />
      )
    },
    {
      title: <SText text={t("warehouse.count")} />,
      key: "count",
      width: 160,
      render: (_, record) => <SText text={record.count?.toString() || ""} style={{ width: "100%" }} />
    },

  ];

  return (
    <Modal width={1200} open={open} onCancel={onClose} onOk={handleOk} afterClose={() => setSelectedRows([])} maskClosable={false}>
      <Spin spinning={loading}>
        <Flex vertical style={{ height: "60vh" }} >
          <STableLayout
            columns={columns as Array<AnyObject>}
            dataSource={list}
            total={total}
            rowKey="productId"
            headerChildren={
              <Input.Search
                enterButton
                placeholder={t("requirement.searchPro")}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onSearch={(e) => fetch(0)}
              />
            }
            extHeight={40}
            tableLayoutRef={tableLayoutRef}
            handleChange={fetch}
            rowSelection={{
              selectedRowKeys: selectedRows.map(row => row.productId),
              preserveSelectedRowKeys: true,
              onChange: (_, newSelectedRows) => setSelectedRows(newSelectedRows as FactoryInventory[]),
            }}
          />
        </Flex>
      </Spin>
    </Modal>
  )
};

export default SelectFactory;
